import { ILogger } from '@trimble/logging';
import { Vector2 } from '@technology/web3d';
import type { FeaturesServiceCapabilityProvider } from '@trimble/web3d-providers';
import { type CadSceneProjection } from './cad-scene-projection';
import { modelIdForRootTile, ROOT_ZOOM_LEVEL } from './feature-tile-config';
import type { PointCloudRoot } from './feature-extraction-types';
import type {
  RootTileGrouper,
  RootTileTarget,
} from './feature-root-tiles.types';

export function createRootTileGrouper(params: {
  readonly logger: ILogger;
  readonly projection: Pick<CadSceneProjection, 'converter'>;
  readonly featuresProvider: Pick<
    FeaturesServiceCapabilityProvider,
    'getTileCoordsFromLatLon'
  >;
}): RootTileGrouper {
  const { logger, projection, featuresProvider } = params;
  const warnedRootIds = new Set<string>();

  const resolveRootTile = (root: PointCloudRoot): Vector2 => {
    try {
      // Plugin proj4 cannot resolve TGL scene projection.
      const lonLat = projection
        .converter()
        ?.sceneToLonLat(root.center.x, root.center.y);
      if (!lonLat) {
        throw new Error('scene->lon/lat conversion unavailable');
      }
      const tile = featuresProvider.getTileCoordsFromLatLon(
        lonLat.lat,
        lonLat.lon,
        ROOT_ZOOM_LEVEL
      );
      return new Vector2(tile.x, tile.y);
    } catch (error) {
      if (!warnedRootIds.has(root.id)) {
        warnedRootIds.add(root.id);
        logger.warn(
          'Failed to derive feature tile root coordinate; defaulting to origin',
          { pointCloudId: root.id },
          error instanceof Error ? error : new Error(String(error))
        );
      }
      return new Vector2(0, 0);
    }
  };

  return (roots) => {
    const targets = new Map<string, RootTileTarget>();
    for (const root of roots) {
      const rootCoordinate = resolveRootTile(root);
      const modelId = modelIdForRootTile(rootCoordinate.x, rootCoordinate.y);
      const target = targets.get(modelId);
      if (target) {
        target.pointCloudIds.push(root.id);
      } else {
        targets.set(modelId, { rootCoordinate, pointCloudIds: [root.id] });
      }
    }
    return targets;
  };
}
